/**
 * useTranslate — Translate chat message text via backend (Gemini)
 *
 * Flow:
 *   1. User taps translate icon on a message bubble
 *   2. POST /api/v1/voice/translate sends text + target lang to Gemini
 *   3. Backend returns translated text
 *   4. Translation is shown inline below the message
 */

import { useState, useCallback } from 'react';
import { api } from '@/lib/api';

export const TRANSLATE_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'zh', label: '中文' },
  { code: 'zh-Hant', label: '繁體中文' },
  { code: 'ms', label: 'Bahasa Melayu' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'th', label: 'ไทย' },
  { code: 'vi', label: 'Tiếng Việt' },
] as const;

export type TranslateLang = (typeof TRANSLATE_LANGUAGES)[number]['code'];

export interface TranslationEntry {
  messageId: string;
  lang: TranslateLang;
  text: string;
}

export function useTranslate() {
  /** Map of messageId → translation text */
  const [translations, setTranslations] = useState<Map<string, TranslationEntry>>(new Map());
  const [translating, setTranslating] = useState<string | null>(null);

  const translate = useCallback(async (messageId: string, text: string, targetLang: TranslateLang) => {
    if (!text.trim()) return;
    setTranslating(messageId);

    try {
      const res = await api.post<{ text: string; lang: string }>('/voice/translate', {
        text: text.slice(0, 5000),
        targetLang,
      });

      const entry: TranslationEntry = {
        messageId,
        lang: targetLang,
        text: res.text ?? '',
      };

      setTranslations(prev => {
        const next = new Map(prev);
        next.set(messageId, entry);
        return next;
      });
    } catch {
      // silently fail — user can retry
    } finally {
      setTranslating(null);
    }
  }, []);

  const clearTranslation = useCallback((messageId: string) => {
    setTranslations(prev => {
      const next = new Map(prev);
      next.delete(messageId);
      return next;
    });
  }, []);

  return {
    translations,
    translating,
    translate,
    clearTranslation,
  };
}
